/**
 * GroupingOverviewPage — what actually sits under a province, district, country
 * office or HQ: its assemblies, the people in them, what is coming up, and what
 * has been given. Admin-only; groupings never carry members of their own.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { GroupingLeadersCard } from '@/components/church/GroupingLeadersCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Building2, Users, CalendarDays, Clock, Loader2, Coins, Network,
} from 'lucide-react';

const money = (byCur) => {
  const entries = Object.entries(byCur || {});
  if (entries.length === 0) return '—';
  return entries.map(([cur, v]) => `${cur} ${Number(v).toLocaleString()}`).join(' · ');
};

const Tile = ({ label, value, sub, testId, icon: Icon }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-500">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      <p className="text-[11px] uppercase tracking-wide">{label}</p>
    </div>
    <p className="text-2xl font-bold text-slate-900 mt-1">{value ?? '—'}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

export default function GroupingOverviewPage() {
  const { churchId, shareToken } = useParams();
  const { user } = useAuthStore();
  const isSuper = !!user && (user.is_super_admin === true || user.role === 'super_admin'
    || (user.permissions || []).includes('*'));
  const shared = !!shareToken;

  const { data, isLoading, isError } = useQuery({
    queryKey: shared ? ['shared-grouping-overview', shareToken] : ['grouping-overview', churchId],
    queryFn: () => (shared
      ? fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/grouping-overview/${shareToken}`)
        .then((r) => { if (!r.ok) throw new Error('link expired'); return r.json(); })
      : api.get(`/admin/churches/${churchId}/overview`).then((r) => r.data)),
    enabled: !!(churchId || shareToken),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (isError || !data?.grouping) {
    return (
      <div className="min-h-screen bg-slate-50 p-8" data-testid="grouping-overview-error">
        <p className="text-sm text-slate-500">
          {shared
            ? 'This shared link has expired or been revoked.'
            : 'This organisation could not be loaded, or you do not lead it.'}
        </p>
        {!shared && (
          <Link to="/admin/churches" className="text-sm text-emerald-700 hover:underline">
            Back to organisations
          </Link>
        )}
      </div>
    );
  }

  const g = data.grouping;
  const counts = data.counts || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="grouping-overview-page">
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          {!shared && (
            <Link to={`/admin/churches/${churchId}`} className="text-slate-400 hover:text-slate-700"
                  data-testid="grouping-overview-back">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2 truncate">
              <Network className="h-5 w-5 text-emerald-600" />
              {g.name}
            </h1>
            <p className="text-xs text-slate-500 capitalize">
              {g.level || g.organisation_type} {g.country ? `· ${g.country}` : ''}
              {g.overseer_name ? ` · overseer ${g.overseer_name}` : ''}
            </p>
          </div>
          {shared && (
            <Badge variant="outline" className="ml-auto text-[10px]"
                   data-testid="grouping-overview-shared-badge">
              Shared · read only
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile label="Assemblies" value={counts.assemblies} icon={Building2}
                sub="congregations beneath this grouping" testId="grouping-tile-assemblies" />
          <Tile label="Members" value={counts.members} icon={Users}
                sub="across every assembly" testId="grouping-tile-members" />
          <Tile label="Sub-groupings" value={counts.sub_groupings} icon={Network}
                sub="provinces, districts, country offices" testId="grouping-tile-subgroupings" />
          {!shared && (
            <Tile label="Giving (30 days)" value={money(data.giving?.last_30_days_by_currency)}
                  icon={Coins} sub={`all time ${money(data.giving?.total_by_currency)}`}
                  testId="grouping-tile-giving" />
          )}
        </div>

        {(data.assemblies_without_services || []).length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4"
               data-testid="grouping-missing-services">
            <p className="text-sm font-medium text-amber-900">
              {data.assemblies_without_services.length} assembl
              {data.assemblies_without_services.length === 1 ? 'y has' : 'ies have'} no public
              service times — visitors to this page cannot see when to turn up.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {data.assemblies_without_services.slice(0, 8).map((a) => (
                shared ? (
                  <span key={a.id} className="text-xs text-amber-800"
                        data-testid={`missing-services-${a.id}`}>{a.name}</span>
                ) : (
                  <Link key={a.id} to={`/admin/churches/${a.id}`}
                        className="text-xs text-amber-900 underline hover:no-underline"
                        data-testid={`missing-services-${a.id}`}>
                    {a.name}
                  </Link>
                )
              ))}
              {data.assemblies_without_services.length > 8 && (
                <span className="text-xs text-amber-800" data-testid="missing-services-more">
                  and {data.assemblies_without_services.length - 8} more
                </span>
              )}
            </div>
          </div>
        )}

        <Card data-testid="grouping-assemblies-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-emerald-600" /> Assemblies
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(data.assemblies || []).length === 0 ? (
              <p className="p-6 text-sm text-slate-500" data-testid="grouping-assemblies-empty">
                No congregations sit under this grouping yet.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-slate-500 border-b">
                    <th className="text-left py-2 px-4">Assembly</th>
                    <th className="text-left py-2 px-4">Where</th>
                    <th className="text-right py-2 px-4">Services</th>
                    <th className="text-right py-2 px-4">Members</th>
                  </tr>
                </thead>
                <tbody>
                  {data.assemblies.map((a) => (
                    <tr key={a.id} className="border-b last:border-0 hover:bg-slate-50"
                        data-testid={`grouping-assembly-${a.id}`}>
                      <td className="py-2 px-4">
                        {shared ? (
                          <span className="font-medium text-slate-800">{a.name}</span>
                        ) : (
                          <Link to={`/admin/churches/${a.id}`}
                                className="font-medium text-slate-800 hover:text-emerald-700">
                            {a.name}
                          </Link>
                        )}
                        <Badge variant="outline" className="ml-2 text-[10px] capitalize">{a.level}</Badge>
                      </td>
                      <td className="py-2 px-4 text-xs text-slate-500">
                        {[a.city, a.country].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="py-2 px-4 text-right text-xs"
                          data-testid={`grouping-assembly-services-${a.id}`}>
                        {a.public_services > 0
                          ? <span className="text-slate-600">{a.public_services} public</span>
                          : <span className="text-amber-700">none published</span>}
                      </td>
                      <td className="py-2 px-4 text-right font-medium">{a.members ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="grouping-upcoming-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-indigo-600" /> Coming up
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(data.upcoming || []).length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="grouping-upcoming-empty">
                  Nothing dated in the next 60 days.
                </p>
              ) : data.upcoming.map((u, i) => (
                <div key={`${u.title}-${i}`} className="flex items-start justify-between gap-3 border-b last:border-0 pb-2"
                     data-testid={`grouping-upcoming-${i}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{u.title}</p>
                    <p className="text-xs text-slate-500">
                      {u.church_name}{u.location ? ` · ${u.location}` : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-medium text-slate-700">{u.date}</p>
                    <p className="text-[11px] text-slate-400">{u.start_time || ''}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card data-testid="grouping-services-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" /> Weekly services
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(data.weekly_services || []).length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="grouping-services-empty">
                  No weekly service patterns recorded under this grouping.
                </p>
              ) : data.weekly_services.map((s, i) => (
                <div key={`${s.name}-${i}`} className="flex items-start justify-between gap-3 border-b last:border-0 pb-2"
                     data-testid={`grouping-service-${i}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{s.name}</p>
                    <p className="text-xs text-slate-500">
                      {s.church_name}{s.location ? ` · ${s.location}` : ''}
                    </p>
                  </div>
                  <p className="text-xs font-medium text-slate-700 shrink-0">
                    {[s.weekday, s.start_time].filter(Boolean).join(' ')}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {!shared && isSuper && <GroupingLeadersCard churchId={churchId} />}
      </main>
    </div>
  );
}
